/* eslint-disable @typescript-eslint/no-explicit-any */
import axios, { AxiosError } from "axios";
import Cookies from "js-cookie";

const API_URL = "/api/auth/signin";

// Axios defaults
axios.defaults.withCredentials = true;
axios.defaults.headers.post["Content-Type"] = "application/json";

/** === API shapes === */
export type ApiUser = {
  name: string;
  email: string;
  role: "admin" | string;
  accessToken: string;
  refreshToken?: string;
};

export type ApiEnvelope = {
  message?: string;
  data?: ApiUser;
};

export interface LoginInput {
  email?: string;
  phone?: string;
  password: string;
}

/** What `login()` returns (you currently wrap axios data inside `{ data: … }`) */
export type LoginResponse = { data: ApiEnvelope };

export const login = async (input: LoginInput): Promise<LoginResponse> => {
  try {
    const response = await axios.post<ApiEnvelope>(API_URL, input);

    const user = response.data?.data;
    if (!user?.accessToken) {
      throw new Error("Invalid login response");
    }

    // Persist tokens in cookies (days)
    Cookies.set("accessToken", user.accessToken, {
      expires: 1,
      sameSite: "lax",
    });
    if (user.refreshToken) {
      Cookies.set("refreshToken", user.refreshToken, {
        expires: 7,
        sameSite: "lax",
      });
    }
    Cookies.set("userRole", user.role, { expires: 1, sameSite: "lax" });

    // Set Authorization header for subsequent axios calls
    axios.defaults.headers.common[
      "Authorization"
    ] = `Bearer ${user.accessToken}`;

    // Keep your current return shape
    return { data: response.data };
  } catch (error) {
    const axiosError = error as AxiosError<{
      detail?: string;
      non_field_errors?: string[];
      message?: string;
    }>;

    const apiMsg =
      axiosError.response?.data?.detail ||
      axiosError.response?.data?.message ||
      axiosError.response?.data?.non_field_errors?.[0];

    throw new Error(
      apiMsg || axiosError.message || "Unable to connect to the server"
    );
  }
};

export const setAuthToken = (token: string | null) => {
  if (token) {
    Cookies.set("accessToken", token, { expires: 1, sameSite: "lax" });
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  } else {
    Cookies.remove("accessToken");
    delete axios.defaults.headers.common["Authorization"];
  }
};

export const getAuthToken = (): string | undefined =>
  Cookies.get("accessToken");
export const getUserRole = (): string | undefined => Cookies.get("userRole");
export const isAuthenticated = (): boolean =>
  Boolean(Cookies.get("accessToken"));
